import AppModal from 'components/common/modal/AppModal';
import PlainModal from 'components/common/modal/PlainModal';
import Project from 'components/project/Project';
import { handleClearPmsData, pmsViewThumbnail } from 'containers/base/PmsConnect';
import CompleteProjectModalContainer from 'containers/project/CompleteProjectModalContainer';
import { PrivateSocketContext } from 'contexts/PrivateSocketContext';
import { ProjectSocketContext } from 'contexts/ProjectSocketContext';
import { ProjectContext } from 'contexts/project/ProjectContext';
import useFetchLoading from 'lib/hooks/useFetchLoading';
import useInput from 'lib/hooks/useInput';
import { pageUrl } from 'lib/mapper';
import storage, { keys } from 'lib/storage';
import { IProjectInfo } from 'lib/types/projectTypes';
import { useDidUpdateEffect } from 'lib/utils';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  BaseActions,
  EmployeeActions,
  FileActions,
  ProjectActions,
  UtilActions,
} from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { isClinicUserSelector, isNormalLevelUserSelector } from 'store/modules/user';

export default function ProjectContainer() {
  const {
    language,
    user,
    indicationFormat,
    indicationFormatSuccess,
    indicationInfo,
    indicationInfoSuccess,
    projectData,
    fetchProjectSuccess,
    fetchProjectFailure,
    editProjectSyncSuccess,
    editTimeline,
    editTimelineSuccess,
    isNormalLevelUser,
    isClinicUser,
    fetchManagerList: { list: managerList },
    fetchRemakeList: { list: remakeList },
    isPmsReady,
    pmsCommand,
    pmsData,
    isPmsOrder,
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    user: state.user.user,
    indicationFormat: state.util.indicationFormat.data?.indicationFormat,
    indicationFormatSuccess: state.util.indicationFormat.success,
    indicationInfo: state.util.indicationInfo.data?.indicationInfo,
    indicationInfoSuccess: state.util.indicationInfo.success,
    projectData: state.project.project.data,
    fetchProjectSuccess: state.project.project.success,
    fetchProjectFailure: state.project.project.failure,
    editProjectSyncSuccess: state.project.editProjectSync.success,
    editTimeline: state.project.editTimeline.data?.payload?.stage,
    editTimelineSuccess: state.project.editTimeline.success,
    isNormalLevelUser: isNormalLevelUserSelector(state),
    isClinicUser: isClinicUserSelector(state),
    fetchManagerList: state.employee.managerList.data || {},
    fetchRemakeList: state.util.remakeList.data || {},
    isPmsReady: state.pms.isPmsReady,
    pmsCommand: state.pms.command,
    pmsData: state.pms.pmsData,
    isPmsOrder: state.pms.isPmsOrder,
  }));
  const { t } = useTranslation();
  const { projectAcceptEvent } = useContext(PrivateSocketContext);
  const { newProjectChat } = useContext(ProjectSocketContext);
  const [nowProjectTab, setNowProjectTab] = useState(0);

  const projectTabList = [t('PROJECT_INFORMATION'), t('FILE_SYNC')];
  const handleClickProjectTab = (tab: number) => {
    setNowProjectTab(tab);
  };
  const { onInitLoadLocalFiles, reworkProjectCode, onListenerLocalFiles, localFileListCt } =
    useContext(ProjectContext);
  const { pcode: projectCode } = useParams() as { pcode: string };
  const projectInfo: IProjectInfo = projectData?.projectInfo;
  const syncInfo = projectInfo?.syncInfo;
  const timeline = projectInfo?.timeline;
  const isExistInvoice = projectInfo?.invoiceInfo?.isExist;
  const clientIdx = projectInfo?.clientIdx;
  const ownerIdx = projectInfo?.ownerIdx;
  const projectStep = useInput(1);
  const partnerManager = useInput(0);
  const [prevPartnerManager, setPrevPartnerManager] = useState<number | null>(null);
  const isNew = !projectInfo?.ownerGroupCode;
  const isOwner = isNew ? isNew : user?.groupCode === projectInfo.ownerGroupCode;
  const setProjecStepValue = useMemo(() => {
    return fetchProjectSuccess && projectStep.setValue(projectInfo?.stage);
  }, [fetchProjectSuccess]);
  // NOTE: notifications mapping
  // ISSUE: notifications sync 알림이 현재 그룹원(매니저)에게 오지 않음! 확인 필요
  const hasAlarm =
    syncInfo?.syncStatus === 0 &&
    syncInfo?.userGroupIdx === user.userGroupIdx &&
    user.authority <= 1;
  const hasOwnTimeline = timeline?.hasOwnProperty('new') && !!timeline['new'];
  const [completeProjectModalOpen, setCompleteProjectModalOpen] = useState(false);
  const { ipcRenderer } = window;
  const [isShowChat, setIsShowChat] = useState<boolean>(false);
  const [goldVisible, setGoldVisible] = useState(false);
  const { isChatOpen } = useContext(ProjectSocketContext);
  const [isProjectCreator, setIsProjectCreator] = useState<boolean>(false);

  const location = useLocation();
  const isFromCreateComponent = !!location?.state?.isFromCreateComponent;
  const isFromEditComponent = !!location?.state?.isFromEditComponent;
  const history = useHistory();

  const [goldStatusModalOpen, setGoldStatusModalOpen] = useState(false);

  const [eventTargetRef, setEventTargetRef] = useState<any>();
  const [isProjectGet, setIsProjectGet] = useState(false);

  const handleCloseGoldStatusModal = () => setGoldStatusModalOpen(!goldStatusModalOpen);

  // SECTION: function
  const handleChangePartnerManager = (number: number) => {
    ProjectActions.edit_project_manager_request({ projectCode, data: { employeeIdx: number } });
    setPrevPartnerManager(number);
  };
  const handleCloseCompleteProjectModal = () => setCompleteProjectModalOpen(false);
  const handleOpenCompleteProjectModal = () => setCompleteProjectModalOpen(true);
  const handleEditProjectSync = (syncStatus: number) => {
    ProjectActions.edit_project_sync_request({
      projectCode,
      syncStatus,
      ownerSyncUid: projectInfo?.ownerSyncUid,
    });

    if (syncStatus === 2) {
      history.go(-1);
    }
  };

  const handleClickGoToBack = () => {
    if (isFromEditComponent) {
      history.go(-2);
    } else {
      history.goBack();
    }
  };

  const handleClickChat = () => {
    setIsShowChat(!isShowChat);
    isChatOpen.setValue(!isShowChat);
  };

  useEffect(() => {
    return () => {
      isChatOpen.setValue(false);
    };
  }, []);

  useEffect(() => {
    if (isProjectCreator) {
      if (clientIdx === ownerIdx || !clientIdx) {
        setGoldVisible(false);
      } else {
        setGoldVisible(true);
      }
    } else {
      setGoldVisible(true);
    }
  }, [isProjectCreator, clientIdx, ownerIdx]);

  useEffect(() => {
    ProjectActions.set_project_invite_alarm(hasAlarm);
  }, [hasAlarm]);

  useDidUpdateEffect(() => {
    if (projectCode === projectAcceptEvent.value) {
      ProjectActions.fetch_project_request(projectCode);
    }
  }, [projectAcceptEvent.value]);

  useDidUpdateEffect(() => {
    // NOTE: 프로젝트 권한 판별
    if (user?.groupCode === projectInfo?.ownerGroupCode) {
      setIsProjectCreator(true);
    } else {
      setIsProjectCreator(false);
    }
  }, [projectInfo, user]);

  useDidUpdateEffect(() => {
    if (!fetchProjectSuccess) return;
    newProjectChat.setValue(projectData.newChatCount);
    partnerManager.setValue(projectInfo.partnerManagerIdx);
    setPrevPartnerManager(projectInfo.partnerManagerIdx);
    UtilActions.fetch_remake_type_list_request({ groupCode: projectInfo?.ownerGroupCode });

    if (!ipcRenderer) return;
    if (!isPmsOrder) return;
    if (pmsCommand === pmsViewThumbnail) {
      console.log('PMS] Project ProjectTab [Change] ________');
      // setNowProjectTab(1);
      handleClickProjectTab(1);
    }
  }, [fetchProjectSuccess, pmsCommand, isPmsOrder]);

  useDidUpdateEffect(() => {
    if (!fetchProjectFailure) return;
    history.push(pageUrl.project.list);
    // AppActions.show_toast({ type: 'error', message: projectData.message });
  }, [fetchProjectFailure]);

  useDidUpdateEffect(() => {
    if (!ipcRenderer || !projectInfo) return;
    ipcRenderer.send('projectGet', { projectInfo });
    ipcRenderer.on('projectGetRes', (e: any, data: any) => {
      setIsProjectGet(true);
    });
  }, [projectInfo]);

  // useEffect(() => {
  //   console.log('PMS] Project ViewThumbnail pmsCommand ________', pmsCommand);
  //   if (!isPmsOrder || !isPmsReady) return;
  //   if (pmsCommand === pmsViewThumbnail) {
  //     console.log('PMS] Project ProjectTab [Change] ________');
  //     // setNowProjectTab(1);
  //     handleClickProjectTab(1);
  //   }
  // }, [pmsCommand, isPmsOrder, isPmsReady]);

  useDidUpdateEffect(() => {
    if (!ipcRenderer || !projectInfo) return;

    if (isProjectGet) {
      ipcRenderer.send('watchStart', { projectId: projectInfo?.projectId });
    }
  }, [projectInfo, isProjectGet]);

  useDidUpdateEffect(() => {
    if (!ipcRenderer) return;

    if (!!eventTargetRef) {
      eventTargetRef.removeAllListeners('watchFileUpdate');
    }
    setEventTargetRef(
      ipcRenderer.on('watchFileUpdate', (e: any, data: any) => {
        onListenerLocalFiles(data);
      }),
    );
    // isAbleSyncLocalFile.setValue(true);
  }, [localFileListCt.value, eventTargetRef]);

  useEffect(() => {
    UtilActions.fetch_teeth_indication_info_request({
      groupCode: !!projectInfo?.ownerGroupCode ? projectInfo?.ownerGroupCode : '',
    });
    UtilActions.fetch_teeth_indication_format_request({
      groupCode: !!projectInfo?.ownerGroupCode ? projectInfo?.ownerGroupCode : '',
    });
  }, [projectInfo]);

  useEffect(() => {
    ProjectActions.fetch_project_request(projectCode);
    FileActions.fetch_project_files_request(projectCode);
    EmployeeActions.fetch_manager_list_request();
    // change hasNewEvent on frontend
    ProjectActions.set_projects({ projectCode, hasNewEvent: false });
    reworkProjectCode.setValue(null);
    return () => {
      if (!history.location.state?.isFromCreating) ProjectActions.fetch_project_clear();
      if (!!eventTargetRef) {
        eventTargetRef.removeAllListeners('watchFileUpdate');
      }
      if (!!ipcRenderer) {
        ipcRenderer.send('watchStop');
      }
    };
  }, [projectCode]);

  useEffect(() => {
    if (nowProjectTab === 0) {
      localFileListCt.setValue(
        localFileListCt.value.reduce((acc: any[], curr: any) => {
          curr.isView = false;
          curr.isChecked = false;
          return acc.concat(curr);
        }, []),
      );
    }
  }, [nowProjectTab]);

  // get project after invite accept, reject
  useDidUpdateEffect(() => {
    if (editProjectSyncSuccess) {
      ProjectActions.fetch_project_request(projectCode);
    }
  }, [!!editProjectSyncSuccess]);

  // check invoice, partner and completeModal open
  useDidUpdateEffect(() => {
    if (!!editTimelineSuccess) {
      ProjectActions.fetch_project_request(projectCode);

      if (editTimeline === 7 && !isNormalLevelUser && !isClinicUser) {
        if (isOwner) {
          if (projectInfo.clientIdx !== projectInfo.ownerIdx) {
            handleOpenCompleteProjectModal();
          }
        } else {
          handleOpenCompleteProjectModal();
        }
      }
    }
  }, [!!editTimelineSuccess, projectInfo, isExistInvoice, editTimeline]);

  const { isFetchSuccess } = useFetchLoading({
    indicationFormatSuccess,
    indicationInfoSuccess,
    fetchProjectSuccess,
  });
  if (!isFetchSuccess) return null;
  return (
    <>
      {/* CompleteProject */}
      <PlainModal isOpen={completeProjectModalOpen} width={1280}>
        <AppModal
          title={'Complete project'}
          content={
            <CompleteProjectModalContainer
              projectCode={projectCode}
              onCancel={handleCloseCompleteProjectModal}
            />
          }
          contentCardStyle={{
            borderRadius: '8px',
            marginBottom: '28px',
            padding: '38px 30px 76px',
          }}
          isCloseIcon={true}
          onCancel={handleCloseCompleteProjectModal}
          hideButton={true}
        />
      </PlainModal>

      <Project
        hasOwnTimeline={hasOwnTimeline}
        isOwner={isOwner}
        isShowChat={isShowChat}
        goldVisible={goldVisible}
        goldStatusModalOpen={goldStatusModalOpen}
        language={language}
        hasAlarm={hasAlarm}
        indicationFormat={indicationFormat}
        indicationInfo={indicationInfo}
        projectInfo={projectInfo}
        isProjectCreator={isProjectCreator}
        managerList={managerList}
        remakeList={remakeList}
        prevPartnerManager={prevPartnerManager}
        partnerManager={partnerManager}
        onOpenCompleteProjectModal={handleOpenCompleteProjectModal}
        onChangePartnerManager={handleChangePartnerManager}
        onEditProjectSync={handleEditProjectSync}
        onCloseGoldStatusModal={handleCloseGoldStatusModal}
        onClickGoToBack={handleClickGoToBack}
        onClickChat={handleClickChat}
        nowProjectTab={nowProjectTab}
        projectTabList={projectTabList}
        onClickProjectTab={handleClickProjectTab}
      />
    </>
  );
}
